import React, {useEffect, useMemo, useState} from 'react'
import {Loading} from '@therms/atalaya'
import {PickReportType} from '@src/modules/Reports/components/PickReportType'
import {PickReportDate} from '@src/modules/Reports/components/PickReportDate'
import {EditReportRefs} from '@src/modules/Reports/components/EditReportRefs'
import {Alerts, useRPCContext} from '@therms/react-modules'

interface NewReportStepperProps {
  onReportCreated: (params: {reportId: string}) => void
}

function NewReportStepper({onReportCreated}: NewReportStepperProps) {
  const {rpcService} = useRPCContext()
  const [step, setStep] = useState<string>('')
  const [allowedSteps, setAllowedSteps] = useState<string[]>([])

  const [date, setDate] = useState<string>()
  const [refsByType, setRefsByType] = useState({})
  const [reportTypeId, setReportTypeId] = useState('')
  const [reportType, setReportType] = useState<any>()

  const nextStep = () => {
    const nextStepIndex = allowedSteps.indexOf(step) + 1

    setStep(allowedSteps[nextStepIndex])
  }

  const hasNextStep = useMemo(
    () => !!allowedSteps[allowedSteps.indexOf(step) + 2],
    [allowedSteps, step],
  )

  useEffect(() => {
    if (reportTypeId) {
      rpcService.Reports.GetReportTypesDetails.v1
        .call({reportTypeIds: [reportTypeId]})
        .then(({data: {reportTypes}}) => {
          const _reportType = reportTypes[0]

          setReportType(_reportType)

          const steps: string[] = []

          if (_reportType?.dateConfig?.initialDate === 'user-selected') {
            steps.push('date')
          }

          if (Object.values(_reportType.refsConfig).find((x) => !!x)) {
            steps.push('refs')
          }

          steps.push('done')

          setAllowedSteps(steps)
          setStep(steps[0])
        })
        .catch((err: any) => {
          console.log('Reports.GetReportTypesDetails.v1 err', err)

          Alerts.error(
            'There was a problem getting the Report Type information, please try again',
            err.message,
          )
        })
    }
  }, [reportTypeId])

  useEffect(() => {
    if (step === 'done') {
      rpcService.Reports.CreateReport.v1
        .call({
          report: {
            date,
            refsByType,
            reportTypeId,
          },
        })
        .then(({data: {reportId}}) => {
          onReportCreated({reportId})
        })
        .catch((err: any) => {
          console.log('Reports.CreateReport.v1 err', err)

          Alerts.error(
            'There was a problem creating the Report, please try again',
            err.message,
          )
        })
    }
  }, [allowedSteps, step])

  if (!reportType) {
    return <PickReportType onSelectReportType={({id}) => setReportTypeId(id)} />
  }

  if (step === 'date') {
    return (
      <PickReportDate
        actionLabel={hasNextStep ? 'Next' : 'Create Report'}
        onChangeValue={setDate}
        onComplete={nextStep}
        value={date}
      />
    )
  }

  if (step === 'refs') {
    return (
      <EditReportRefs
        actionLabel={hasNextStep ? 'Next' : 'Create Report'}
        allowedRefs={reportType?.refsConfig}
        onChangeValue={setRefsByType}
        onComplete={nextStep}
        value={refsByType}
      />
    )
  }

  return <Loading overlay />
}

export {NewReportStepper}
